import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { RequestService } from '../services/RequestService'
import { filterRows } from '../utils/filterRowsByTerm'
import { useSnackbar } from '../hooks'
import { STATUS } from '../utils/notificationStatus'

const NotificationContext = createContext()

const today = new Date()
const currentYear = today.getFullYear()
const currentMonth = today.getMonth()
const startDate = new Date(currentYear, currentMonth, 1)

export const NotificationProvider = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [resetRowsState, setResetRowsState] = useState([])
  const [filterOptions, setFilterOptions] = useState(null)
  const [selectedFilterOptions, setSelectedFilterOptions] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [dateRange, setDateRange] = useState([startDate, new Date()])
  const [selectedTab, setSelectedTab] = useState(0)
  const { errorSnackbar } = useSnackbar()

  const handleType = e => {
    const inputValue = e.target.value
    setInputValue(inputValue)
    filterRows({
      filterByTerm: inputValue,
      rows: filterRowsByOptions(),
      defineRows: setRows,
    })
  }

  const createRows = ({ data }) => {
    const rows = data?.map(
      ({
        id,
        employee,
        message,
        date,
        status,
        severity,
        shift,
        position,
        team,
      }) => ({
        id,
        employee,
        message,
        date,
        status,
        shift,
        severity,
        position: position?.title,
        level: position?.level,
        team,
      }),
    )

    setResetRowsState(rows)

    return rows || []
  }

  const filterRowsByOptions = () => {
    let rows = resetRowsState

    if (selectedFilterOptions?.shifts.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.shifts.includes(row.shift),
      )
    }

    if (selectedFilterOptions?.levels.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.levels.includes(row.level),
      )
    }

    if (selectedFilterOptions?.positions.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.positions.includes(row.position),
      )
    }

    if (selectedFilterOptions?.teams.length > 0) {
      rows = rows.filter(row => selectedFilterOptions.teams.includes(row.team))
    }

    if (selectedFilterOptions?.notificationsStatuses.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.notificationsStatuses.includes(row.status),
      )
    }

    if (selectedFilterOptions?.severities.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.severities.includes(row.severity),
      )
    }

    return rows
  }

  const getPossibleFilters = ({ data }) => {
    const filters = {
      shifts: [],
      teams: [],
      positions: [],
      levels: [],
      notificationsStatuses: [],
      severities: [],
    }

    data.forEach(({ shift, team, position, status, severity }) => {
      if (shift && !filters.shifts.includes(shift)) filters.shifts.push(shift)

      if (team && !filters.teams.includes(team)) filters.teams.push(team)

      if (position?.title && !filters.positions.includes(position.title))
        filters.positions.push(position.title)

      if (position?.level && !filters.levels.includes(position.level))
        filters.levels.push(position.level)

      if (status && !filters.notificationsStatuses.includes(status))
        filters.notificationsStatuses.push(status)

      if (severity && !filters.severities.includes(severity))
        filters.severities.push(severity)
    })

    return filters
  }

  const handleDateRange = ({ value }) => {
    const newValue = !value ? [] : value
    setDateRange(newValue)
  }

  const handleChangeSelectedTab = (_, newValue) => {
    setSelectedTab(newValue)
    handleDateRange({ value: [startDate, new Date()] })
  }

  const onRead = async ({ id, acknowledged }) => {
    try {
      const { status } = await RequestService.readNotification({
        id,
        acknowledged: !acknowledged,
      })

      if (status === 'success') {
        const notification = rows.findIndex(row => row.id === id)
        let rowsCopy = rows

        rowsCopy[notification].status =
          rowsCopy[notification].status === STATUS.ACKNOWLEDGED
            ? STATUS.PENDING
            : STATUS.ACKNOWLEDGED

        setRows([...rowsCopy])
      }
    } catch (e) {
      errorSnackbar({
        text: 'Houve um problema ao marcar a notificação como lida',
      })
    }
  }

  const fetchData = useCallback(
    async ({ dateRange }) => {
      setLoading(true)
      try {
        let response = null
        if (selectedTab === 0) {
          response = await RequestService.getReceivedNotifications({
            startDate: dateRange[0],
            endDate: dateRange[1],
          })
        }

        if (selectedTab === 1) {
          response = await RequestService.getSentNotifications({
            startDate: dateRange[0],
            endDate: dateRange[1],
          })
        }
        const { data } = response

        const rows = createRows({ data })
        const filters = getPossibleFilters({ data })

        setFilterOptions(filters)
        setRows(rows)
      } catch (e) {
        console.log('useNotificationsPage', e)
      }

      setLoading(false)
    },
    [selectedTab],
  )

  useEffect(() => {
    const filteredRows = filterRowsByOptions()

    if (resetRowsState.length > 0) {
      setRows(filteredRows)
      setInputValue('')
    }
  }, [selectedFilterOptions])

  useEffect(() => {
    if (dateRange) {
      localStorage.setItem('@ialerta/reset-filters', true)
      setInputValue('')
      setSelectedFilterOptions(null)
    }
  }, [dateRange])

  return (
    <NotificationContext.Provider
      value={{
        loading,
        rows,
        filterOptions,
        setSelectedFilterOptions,
        inputValue,
        handleType,
        dateRange,
        handleDateRange,
        selectedTab,
        handleChangeSelectedTab,
        selectedFilterOptions,
        onRead,
        fetchData,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotificationsPage = () => {
  return useContext(NotificationContext)
}
